import { compact, mapValues } from 'lodash';
import { listToQueryParam } from '../routes';

const toQueryParams = (params: {
  [key: string]: string | undefined | null;
}) => {
  return compact(
    Object.entries(params).map(([key, value]) => {
      return value ? `${key}=${value}` : null;
    })
  ).join('&');
};

const withQueryParams = (
  path: string,
  params: { [key: string]: string | undefined | null }
) => {
  const queryParams = toQueryParams(params);
  return queryParams ? `${path}?${queryParams}` : path;
};

export const ROUTES = {
  /**
   * @deprecated
   */
  overview: {
    path: `/experience/:accessToken`,
    url: (accessToken: string) => `/experience/${accessToken}`
  },
  /**
   * @deprecated
   */
  taskDetails: {
    path: `/experience/:accessToken/task/:taskId`,
    url: (accessToken: string, taskId: string) =>
      `/experience/${accessToken}/task/${taskId}`
  },
  /**
   * @deprecated
   */
  subTaskDetails: {
    path: `/experience/:accessToken/task/:taskId/subtask/:subTaskId`,
    url: (accessToken: string, taskId: string, subTaskId: string) =>
      `/experience/${accessToken}/task/${taskId}/subTask/${subTaskId}`
  },

  legacy: {
    overview: {
      path: `/v1/experience/:accessToken`,
      url: (accessToken: string) => `/v1/experience/${accessToken}`
    },
    taskDetails: {
      path: `/v1/experience/:accessToken/task/:taskId`,
      url: (accessToken: string, taskId: string) =>
        `/v1/experience/${accessToken}/task/${taskId}`
    },
    subTaskDetails: {
      path: `/v1/experience/:accessToken/task/:taskId/subtask/:subTaskId`,
      url: (accessToken: string, taskId: string, subTaskId: string) =>
        `/v1/experience/${accessToken}/task/${taskId}/subTask/${subTaskId}`
    },
    momentDetails: {
      path: '/v1/m/:accessToken',
      url: (accessToken: string) => `/v1/m/${accessToken}`
    }
  },

  enrollmentForms: {
    path: '/enrollmentForms/:accessToken',
    url: (accessToken: string) => `/enrollmentForms/${accessToken}`
  },
  momentDetails: {
    path: '/m/:accessToken',
    url: (accessToken: string) => `/m/${accessToken}`
  }
};

const withCarriedOverImpersonateParams = (params: {
  [key: string]: string | undefined | null;
}) => {
  if (window?.location.search.includes('impersonate=true')) {
    return { ...params, impersonate: 'true' };
  }
  return params;
};

const withEncodedReturnTo = (params: {
  [key: string]: string | undefined | null;
}) => {
  return mapValues(params, (v, k) => {
    if (k === 'returnTo' && v) {
      return encodeURIComponent(v);
    }
    return v;
  });
};

const withStringifiedListParams = (params: {
  [key: string]: string | string[] | undefined | null;
}) => {
  return mapValues(params, (v, k) => {
    return v && Array.isArray(v) ? listToQueryParam(v) : v;
  });
};

export const ROUTES_V2 = {
  momentSingle: {
    path: '/v2/news/:momentInstanceAccessToken',
    url: (momentInstanceAccessToken: string) => {
      return `/v2/news/${momentInstanceAccessToken}`;
    }
  },
  instance: {
    path: '/v2/experience/:instanceAccessToken',
    url: (
      actorAccessToken: string, // currently unused, will come back later
      instanceAccessToken: string,
      queryParams: {
        w?: number;
        g?: string;
      } = {}
    ) => {
      const p = `/v2/experience/${instanceAccessToken}`;
      const { w, g } = queryParams;
      return withQueryParams(
        p,
        withEncodedReturnTo(
          withCarriedOverImpersonateParams({
            w: w === undefined ? undefined : w.toString(),
            g: g === undefined ? undefined : g
          })
        )
      );
    }
  },
  taskDetails: {
    path: '/v2/experience/:instanceAccessToken/task/:taskId',
    url: (
      actorAccessToken: string, // currently unused, will come back later
      instanceAccessToken: string,
      taskId: string,
      returnTo?: string
    ) => {
      return withQueryParams(
        `/v2/experience/${instanceAccessToken}/task/${taskId}`,
        withCarriedOverImpersonateParams(withEncodedReturnTo({ returnTo }))
      );
    }
  },
  // This is not really in use and just there as an alias for the normal
  // task details page. It exists for backward compatibility reasons when
  // people are coming from emails which might still produce such a specific link
  subTaskDetails: {
    path: '/v2/experience/:instanceAccessToken/task/:taskId/subtask/:subTaskId',
    url: (
      actorAccessToken: string, // currently unused, will come back later
      instanceAccessToken: string,
      taskId: string,
      returnTo?: string
    ) => {
      return withQueryParams(
        `/v2/experience/${instanceAccessToken}/task/${taskId}`,
        withCarriedOverImpersonateParams(withEncodedReturnTo({ returnTo }))
      );
    }
  },
  messageDetails: {
    path: '/v2/experience/:instanceAccessToken/message/:messageId',
    url: (
      actorAccessToken: string, // currently unused, will come back later
      instanceAccessToken: string,
      messageId: string,
      returnTo?: string
    ) => {
      return withQueryParams(
        `/v2/experience/${instanceAccessToken}/message/${messageId}`,
        withCarriedOverImpersonateParams(withEncodedReturnTo({ returnTo }))
      );
    }
  },
  dashboard: {
    path: '/v2/dashboard/:userAccessToken',
    url: (
      userAccessToken: string,
      queryParams: {
        returnTo?: string;
      } = {}
    ) => {
      return withQueryParams(
        `/v2/dashboard/${userAccessToken}`,
        withCarriedOverImpersonateParams(withEncodedReturnTo(queryParams))
      );
    }
  },
  inbox: {
    path: '/v2/inbox/:userAccessToken',
    url: (
      userAccessToken: string,

      queryParams: {
        g?: string; // selected nav group
        u?: string[] | null; // selected user/participant via public id
        t?: string[] | null;
        returnTo?: string;
        tsort?: string;
      } = {}
    ) => {
      return withQueryParams(
        `/v2/inbox/${userAccessToken}`,
        withEncodedReturnTo(
          withCarriedOverImpersonateParams(
            withStringifiedListParams(queryParams)
          )
        )
      );
    }
  },
  inboxTasks: {
    path: '/v2/inbox/:userAccessToken/tasks/:taskTemplateId',
    url: (
      userAccessToken: string,
      taskTemplateId: string,
      queryParams: {
        g?: string;
        u?: string[] | null;
        t?: string[] | null;
        returnTo?: string;
        i?: string;
        tsort?: string;
        sort?: string;
      } = {}
    ) => {
      return withQueryParams(
        `/v2/inbox/${userAccessToken}/tasks/${taskTemplateId}`,
        withEncodedReturnTo(
          withCarriedOverImpersonateParams(
            withStringifiedListParams(queryParams)
          )
        )
      );
    }
  },
  inboxTask: {
    path:
      '/v2/inbox/:userAccessToken/tasks/:taskTemplateId/task/:instanceAccessToken',
    url: (
      userAccessToken: string,
      taskTemplateId: string,
      instanceAccessToken: string,
      queryParams: {
        g?: string;
        u?: string[] | null;
        t?: string[] | null;
        returnTo?: string;
        tsort?: string;
        sort?: string;
      } = {}
    ) => {
      return withQueryParams(
        `/v2/inbox/${userAccessToken}/tasks/${taskTemplateId}/task/${instanceAccessToken}`,
        withEncodedReturnTo(
          withCarriedOverImpersonateParams(
            withStringifiedListParams(queryParams)
          )
        )
      );
    }
  },

  inboxMessages: {
    path: '/v2/inbox/:userAccessToken/messages/:msgTemplateId',
    url: (
      userAccessToken: string,
      msgTemplateId: string,
      queryParams: {
        g?: string;
        u?: string[] | null;
        t?: string[] | null;
        returnTo?: string;
        i?: string;
        tsort?: string;
        sort?: string;
      } = {}
    ) => {
      return withQueryParams(
        `/v2/inbox/${userAccessToken}/messages/${msgTemplateId}`,
        withEncodedReturnTo(
          withCarriedOverImpersonateParams(
            withStringifiedListParams(queryParams)
          )
        )
      );
    }
  },

  inboxMessage: {
    path:
      '/v2/inbox/:userAccessToken/messages/:msgTemplateId/message/:instanceAccessToken',
    url: (
      userAccessToken: string,
      msgTemplateId: string,
      instanceAccessToken: string,
      queryParams: {
        g?: string;
        u?: string[] | null;
        t?: string[] | null;
        returnTo?: string;
        tsort?: string;
        sort?: string;
      } = {}
    ) => {
      return withQueryParams(
        `/v2/inbox/${userAccessToken}/messages/${msgTemplateId}/message/${instanceAccessToken}`,
        withEncodedReturnTo(
          withCarriedOverImpersonateParams(
            withStringifiedListParams(queryParams)
          )
        )
      );
    }
  },
  inboxMoment: {
    path: '/v2/inbox/:userAccessToken/news/:momentInstanceAccessToken',
    url: (
      userAccessToken: string,
      momentInstanceAccessToken: string,
      queryParams: {
        g?: string;
        u?: string[] | null;
        t?: string[] | null;
        returnTo?: string;
        tsort?: string;
      } = {}
    ) => {
      return withQueryParams(
        `/v2/inbox/${userAccessToken}/news/${momentInstanceAccessToken}`,
        withEncodedReturnTo(
          withCarriedOverImpersonateParams(
            withStringifiedListParams(queryParams)
          )
        )
      );
    }
  }
};

// moments single
// moments archive -> can be shown in a dialog
// moments within inbox -> probably requires a group param?
// - or we just send them to a dedicated moments page and figure the group
//   out dynamically
